import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Board, type Clip, ShortUrlObjectType } from '@air/api/types';
import { Modal } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';

import { NoPermissionToCreateShareLink } from '~/components/NoPermissionToCreateShareLink';
import { SharePrivateClipTabs } from '~/components/SharePrivateClipModal/SharePrivateClipTabs';
import { ReadonlySharePrivateLink } from '~/components/SharePrivateContentModal/ReadonlySharePrivateLink';
import { ShareLinkTeamInviteNudgePanel } from '~/components/SharePrivateContentModal/ShareLinkTeamInviteNudgePanel';
import { ShareModalHeader } from '~/components/SharePrivateContentModal/ShareModalHeader';
import { SharePrivateContentSpinner } from '~/components/SharePrivateContentModal/SharePrivateContentSpinner';
import { SHARE_PRIVATE_CONTENT_MODAL } from '~/constants/testIDs';
import { useShowTurnOnShareLinkNotifications } from '~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications';
import { useClipPermissions } from '~/hooks/useClipPermissions';
import { useShowShareLinkTeamInviteNudgeForModal } from '~/hooks/useShowShareLinkTeamInviteNudgeForModal';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetExistingShortIdInformation } from '~/swr-hooks/shortUrl/useGetExistingShortIdInformation';
import { useGetShortIdForClip } from '~/swr-hooks/shortUrl/useGetShortIdForClip';
import { canSeeAssetVersions } from '~/utils/permissions/assetPermissions';
import { canCreateShortUrl, canEditShortUrl, canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';

type SharePrivateClipModalProps = AirModalProps<{
  clip: Pick<Clip, 'id' | 'assetVersionCount' | 'ext'>;
  trackLocation: AirActionTrackingLocation;
  boardContext: Board['id'] | undefined;
}>;

export const SharePrivateClipModal = memo(
  ({ clip, onClose, trackLocation, boardContext }: SharePrivateClipModalProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
    const { clipPermissions } = useClipPermissions({
      clipId: clip.id,
    });

    const { showToast } = useToasts();
    const [hasCreatedShareLinkInThisSession, setHasCreatedShareLinkInThisSession] = useState(false);

    const canEdit = canEditShortUrl(clipPermissions);
    const canCreate = canCreateShortUrl(clipPermissions);

    const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();
    const { data: shortIdInfo, isLoading: isInitialLoading } = useGetExistingShortIdInformation({
      id: clip.id,
      boardContext,
      type: ShortUrlObjectType.clip,
      canView: canViewShortUrl(clipPermissions),
    });

    const {
      mutate: createShortIdForClip,
      isPending: isCreatingShortIdForClip,
      isError: isErrorCreatingShortIdForClip,
    } = useGetShortIdForClip();

    const hasShortId = !!shortIdInfo;
    const handleShareLinkCreatedFallback = useCallback(() => {
      showTurnOnShareLinkNotifications({ onManageClick: onClose });
    }, [onClose, showTurnOnShareLinkNotifications]);
    const { isShareLinkTeamInviteNudgeVisible, openShareLinkTeamInviteNudgeModal, workspaceName } =
      useShowShareLinkTeamInviteNudgeForModal({
        isShareLinkCreated: hasCreatedShareLinkInThisSession,
        isShareLinkReady: hasShortId,
        location: 'asset_share_link',
        onShareLinkCreatedFallback: handleShareLinkCreatedFallback,
      });

    useEffect(() => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        return;
      }
      if (
        !isInitialLoading &&
        !hasShortId &&
        canCreate &&
        !isCreatingShortIdForClip &&
        !isErrorCreatingShortIdForClip
      ) {
        createShortIdForClip(
          { workspaceId, id: clip.id, boardContext },
          {
            onSuccess: () => {
              setHasCreatedShareLinkInThisSession(true);
            },
            onError: () => {
              showToast('Something went wrong. Please try again later.', {
                color: 'red',
              });
            },
          },
        );
      }
    }, [
      currentWorkspace?.id,
      clip.id,
      canCreate,
      createShortIdForClip,
      isInitialLoading,
      boardContext,
      isCreatingShortIdForClip,
      hasShortId,
      isErrorCreatingShortIdForClip,
      showToast,
    ]);

    const ModalContent = useMemo(() => {
      if (!clipPermissions) {
        return <SharePrivateContentSpinner />;
      } else if (!shortIdInfo) {
        if (canCreate) {
          return <SharePrivateContentSpinner />;
        } else {
          return <NoPermissionToCreateShareLink />;
        }
      } else {
        if (!canEdit) {
          return (
            <ReadonlySharePrivateLink
              objectType={ShortUrlObjectType.clip}
              shareId={shortIdInfo.id}
              objectId={clip.id}
              trackLocation={trackLocation}
            />
          );
        } else {
          return (
            <SharePrivateClipTabs
              clip={clip}
              trackLocation={trackLocation}
              shortIdInfo={shortIdInfo}
              boardContext={boardContext}
              userPermissions={clipPermissions}
            />
          );
        }
      }
    }, [boardContext, canCreate, canEdit, clip, clipPermissions, shortIdInfo, trackLocation]);

    if (showingSubscriptionExpiredModal) {
      return null;
    }

    const modalTitle = `Share a link to ${
      clip.assetVersionCount && !canSeeAssetVersions(shortIdInfo?.permissions) ? `V${clip.assetVersionCount}` : 'asset'
    }`;

    return (
      <Modal
        className="bg-transparent p-0 focus-visible:ring-0"
        data-testid={SHARE_PRIVATE_CONTENT_MODAL}
        dangerouslyBypassFocusLock
        isOpen
        onDismiss={onClose}
      >
        <div className="rounded-6 bg-surface-1 p-6">
          <ShareModalHeader onClose={onClose} title={modalTitle} />
          {ModalContent}
        </div>
        {isShareLinkTeamInviteNudgeVisible && (
          <ShareLinkTeamInviteNudgePanel
            onInviteClick={openShareLinkTeamInviteNudgeModal}
            workspaceName={workspaceName}
          />
        )}
      </Modal>
    );
  },
);

SharePrivateClipModal.displayName = 'SharePrivateClipModal';
